import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, NumberInput } from '@mantine/core';
import { IconArrowRight, IconCurrencyDollar } from '@tabler/icons-react';
import CardModal from '@/components/card-modal';
import { Reservation } from '@turizi/shared';
import { getCurrencies, EXCHANGE_RATE_MAX_DIGITS } from '@turizi/shared';
import { useAddPriceModifier, useRemovePriceModifier, useExchangeRates } from '@/hooks/api-hooks';
import { MoneyIn } from './snippets';

interface ReservationCurrencyExchangeModalProps {
    reservation: Reservation;
    opened: boolean;
    onClose: () => void;
}

export default function ReservationCurrencyExchangeModal({
    reservation,
    opened,
    onClose
}: ReservationCurrencyExchangeModalProps) {
    const { t } = useTranslation();
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

    // Track exchange rates for each source currency: Record<sourceCurrency, rate>
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});

    const addPriceModifier = useAddPriceModifier();
    const removePriceModifier = useRemovePriceModifier();
    const isPending = addPriceModifier.isPending || removePriceModifier.isPending;

    // Query exchange rates from database
    const { data: exchangeRatesData } = useExchangeRates();
    const allExchangeRates = useMemo(() => exchangeRatesData?.data || [], [exchangeRatesData?.data]);

    // Prefill target currency if reservation already has exchange price modifier
    useEffect(() => {
        if (opened && !selectedCurrency) {
            const existingExchangeMod = (reservation?.price_modifiers || []).find(m => m.type === 'exchange');
            if (existingExchangeMod?.currency) {
                setSelectedCurrency(existingExchangeMod.currency);
            }
        }
    }, [opened, reservation?.price_modifiers, selectedCurrency]);

    // Get all unique source currencies among the bookings that differ from target currency
    const sourceCurrencies = useMemo(() => {
        const currencies = new Set<string>();
        (reservation?.service_bookings || []).forEach(booking => {
            if (booking.money_in_currency && booking.money_in_currency !== selectedCurrency && booking.money_in_amount !== null) {
                currencies.add(booking.money_in_currency);
            }
        });
        return Array.from(currencies);
    }, [reservation?.service_bookings, selectedCurrency]);

    const sourceCurrenciesKey = sourceCurrencies.join(',');

    // Get base totals per currency directly from service bookings before exchange
    const totalsByCurrencyIn = useMemo(() => {
        const res: Record<string, number> = {};
        (reservation?.service_bookings || []).forEach(booking => {
            if (booking.money_in_currency && booking.money_in_amount !== null) {
                res[booking.money_in_currency] = (res[booking.money_in_currency] || 0) + Number(booking.money_in_amount || 0);
            }
        });
        return res;
    }, [reservation?.service_bookings]);

    const currencies = getCurrencies(t);

    // When selectedCurrency or sourceCurrencies change, initialize the rates
    useEffect(() => {
        if (!selectedCurrency) {
            setExchangeRates(prev => Object.keys(prev).length === 0 ? prev : {});
            return;
        }

        const newRates: Record<string, number> = {};
        const existingExchangeMods = (reservation?.price_modifiers || []).filter(
            m => m.type === 'exchange' && m.currency === selectedCurrency
        );

        sourceCurrencies.forEach(fromCurr => {
            const existingMod = existingExchangeMods.find(m => m.description?.startsWith(fromCurr));
            if (existingMod && Number(existingMod.value) > 0) {
                newRates[fromCurr] = Number(existingMod.value);
            } else {
                // Find rate in database fromCurrency -> selectedCurrency
                const rateObj = allExchangeRates.find(
                    r => r.from_currency === fromCurr && r.to_currency === selectedCurrency
                );
                if (rateObj) {
                    newRates[fromCurr] = rateObj.rate;
                } else {
                    newRates[fromCurr] = 1; // Default to 1 if not found
                }
            }
        });
        setExchangeRates(newRates);
    }, [selectedCurrency, sourceCurrenciesKey, allExchangeRates, reservation?.price_modifiers]);

    // Handle editing rate for a specific source currency
    const handleRateChange = (fromCurr: string, value: number | string) => {
        setExchangeRates(prev => ({
            ...prev,
            [fromCurr]: typeof value === 'number' ? value : 0
        }));
    };

    // Calculate converted total dynamically
    const convertedTotal = useMemo(() => {
        if (!selectedCurrency) return null;

        let total = 0;
        (reservation?.service_bookings || []).forEach(booking => {
            if (booking.money_in_amount === null) return;
            if (booking.money_in_currency === selectedCurrency) {
                total += booking.money_in_amount;
            } else {
                const rate = exchangeRates[booking.money_in_currency || ''] || 0;
                total += booking.money_in_amount * rate;
            }
        });
        return total;
    }, [selectedCurrency, reservation?.service_bookings, exchangeRates]);

    const canSave = useMemo(() => {
        if (!selectedCurrency || sourceCurrencies.length === 0) return false;
        // Check if all source currencies have a valid exchange rate > 0
        return sourceCurrencies.every(fromCurr => {
            const rate = exchangeRates[fromCurr];
            return rate !== undefined && rate > 0;
        });
    }, [selectedCurrency, sourceCurrencies, exchangeRates]);

    const handleApply = async () => {
        if (!canSave || !selectedCurrency) return;

        try {
            // 1. Remove existing exchange modifiers on this reservation if any
            const existingExchangeModifiers = (reservation?.price_modifiers || []).filter(
                m => m.type === 'exchange'
            );
            for (const mod of existingExchangeModifiers) {
                await removePriceModifier.mutateAsync({ id: mod.id, reservationId: reservation.id });
            }

            // 2. Create new exchange modifier for each source currency with a valid rate
            for (const fromCurr of sourceCurrencies) {
                const rate = exchangeRates[fromCurr];
                if (rate && rate > 0) {
                    await addPriceModifier.mutateAsync({
                        reservationId: reservation.id,
                        data: {
                            section: 'exchange',
                            type: 'exchange',
                            value: rate,
                            currency: selectedCurrency,
                            description: `${fromCurr} → ${selectedCurrency}`
                        }
                    });
                }
            }
            handleClose();
        } catch (error) {
            console.error("Failed to apply currency exchange:", error);
        }
    };

    const handleClose = () => {
        setSelectedCurrency(null);
        setExchangeRates({});
        onClose();
    };

    return (
        <CardModal
            size='w-fit'
            opened={opened}
            onClose={handleClose}
            header={
                <div className="flex items-center gap-2 text-lg font-semibold text-gray-700">
                    <IconCurrencyDollar size={24} stroke={1.5} />
                    {t('select_currency_for_all_bookings')}
                </div>
            }
            footer={
                <div className="flex gap-2 justify-end">
                    <Button
                        variant="subtle"
                        color="black"
                        onClick={handleClose}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        color="black"
                        radius="sm"
                        onClick={handleApply}
                        disabled={!canSave}
                        loading={isPending}
                    >
                        {t('apply_exchange')}
                    </Button>
                </div>
            }
        >
            <div className="space-y-4">

                <div className="solid-box-border flex gap-4">
                    <Select
                        className='flex-1'
                        label={t('currency')}
                        placeholder={t('select_currency')}
                        data={currencies}
                        value={selectedCurrency}
                        onChange={(val) => setSelectedCurrency(val)}
                        searchable
                        clearable
                    />
                    {selectedCurrency && sourceCurrencies.length > 0 && (
                        <div className='flex-1'>
                            {sourceCurrencies.map(fromCurr => (
                                <NumberInput
                                    key={fromCurr}
                                    hideControls
                                    label={`${t('exchange_rate')} (${fromCurr} ⇨ ${selectedCurrency})`}
                                    placeholder={t('enter_exchange_rate')}
                                    value={exchangeRates[fromCurr] ?? ''}
                                    onChange={(val) => handleRateChange(fromCurr, val)}
                                    decimalScale={EXCHANGE_RATE_MAX_DIGITS}
                                    min={0}
                                    className='font-bold'
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex items-baseline gap-2 justify-between">
                    <div className='solid-box-border-tinted flex-1'>
                        {Object.entries(totalsByCurrencyIn).map(([currency, amount]) => (
                            <MoneyIn
                                key={currency}
                                amount={amount}
                                currency={currency}
                                className={currency === selectedCurrency ? 'text-gray-400' : ''}
                            />
                        ))}
                    </div>

                    <IconArrowRight />

                    {selectedCurrency && convertedTotal !== null && (
                        <div className='solid-box-border flex-1'>
                            <MoneyIn
                                amount={convertedTotal}
                                currency={selectedCurrency}
                            />
                        </div>
                    )}
                </div>
            </div>
        </CardModal>
    );
}

